import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { notification } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import { useRecoilState } from 'recoil'
import { listPointState } from 'store/buildingStore'
import { currentAnnotation, currentPovState } from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import {
  Annotation,
  FloorMapElement,
  Point,
  SenceData,
  SenceDataResponse
} from 'types/building'
import { Direction, KrpanoInitProps, Stairway } from 'types/krpano'
import Utils from 'utils'
import Krpano from 'utils/krpano'

export default function useCommunityKrpano() {
  const { id } = useParams()
  const { t } = useTranslation()
  const { navigate } = useDirectory()
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [, setCurrentSelected] = useRecoilState(currentAnnotation)
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)
  const [points] = useRecoilState(listPointState)
  const [currentHgap, setCurrentHgap] = useState(0)
  const [isFistLoading, setIsFistLoading] = useState(true)
  // function get all data for create scene
  const { trigger: getSenceData } = useMutation(`v1/tour/points/file-info`)

  const { data: building, mutate: refreshBuilding } = useSWR<Building>(
    {
      url: `/v1/tour/buildings/${id}`
    },
    {
      revalidateOnMount: false
    }
  )

  const { data: PointData, mutate } = useSWR<FloorMapElement>(
    currentViewer.selectedFloor
      ? {
          url: 'v1/tour/points',
          params: {
            buildingId: id,
            floorId: currentViewer.selectedFloor
          }
        }
      : null,
    {
      revalidateOnMount: false
    }
  )
  const { trigger, isMutating } = useMutation('v1/tour/annotations')

  // handle redirect to next point
  const handleRedirect = () => {
    ;(window as any).handle_redirect = (
      item: any,
      clickh: any,
      clickv: any
    ) => {
      const ponitSelected = PointData?.points?.find(
        (point) => point.id === item
      )
      if (ponitSelected?.image360) {
        const krpano = document.getElementById('embedpano-viewer') as any
        const h = krpano?.get('view.hlookat')
        const v = krpano?.get('view.vlookat')
        const z = krpano?.get('view.fov')

        krpano.call(`
            if(${clickv} GT -50 AND ${clickv} LT +50 AND 1000 GT 120, set(${clickv},0));
            set3dtransition("sphere", ${clickh}, ${clickv}, (min(1000,200)), 0, ${
              currentHgap - Number(ponitSelected.initView.radarHGap)
            }, 0.0, 0.9, easeoutquad);
          `)

        setCurrentPov({
          h: Number(h),
          v: Number(v),
          z
        })

        setCurrentViewer((prev) => ({
          ...prev,
          selectedImage: ponitSelected.image360?.id,
          selectedPoint: ponitSelected.id,
          selectedArea: `${ponitSelected.areaId}`,
          isClickToDirection: true,
          isSelectPointInLeftMenu: false
        }))
      } else {
        notification.error({
          key: 'redirect',
          message: '画像が設定されていません'
        })
      }
    }
  }

  // handle click annotaion
  const handleClickAnnotation = () => {
    ;(window as any).handle_click_annotation = (annotationId: string) => {
      setCurrentSelected({ id: annotationId, isMutating: true })
      !isMutating &&
        trigger(
          {
            method: 'get',
            data: {
              building_id: id,
              floor_id: currentViewer.selectedFloor,
              point_id: currentViewer.selectedPoint
            },
            query: [annotationId]
          },
          {
            onSuccess: (data) => {
              setCurrentSelected(data)
            },
            onError: (error) => {
              setCurrentSelected((prev) => ({
                ...prev,
                isMutating: false
              }))
              if (error.response.data.error.key === 'annotation_not_exists') {
                const krpano = document.getElementById(
                  'embedpano-viewer'
                ) as any
                krpano.call(`remove_annotation(${annotationId})`)
                notification.error({
                  message: t('translation.deletedAnnotationNotice')
                })
              }
              Utils.handleErrorNavigate(error, navigate)
            }
          }
        )
    }
  }

  const handleChangeFloor = () => {
    ;(window as any).handle_change_view_floor = (
      stair_id: string,
      targetFloor: string,
      targetPoint: string,
      clickh: any,
      clickv: any
    ) => {
      const embedpano = document.getElementById('embedpano-viewer') as any
      if (!building?.floors.find((item) => item.id === Number(targetFloor))) {
        notification.error({
          key: 'stair_error',
          message: t('building.destinationNotFound')
        })
        embedpano.call(`removehotspot('stair-${stair_id}')`)
        return
      }
      const h = embedpano?.get('view.hlookat')
      const v = embedpano?.get('view.vlookat')
      const z = embedpano?.get('view.fov')

      embedpano.call(`
        if(${clickv} GT -50 AND ${clickv} LT +50 AND 1000 GT 120, set(${clickv},0));
        set3dtransition("sphere", ${clickh}, ${clickv}, (min(1000,200)), 0, 0, 0.0, 0.9, easeoutquad);
      `)

      setCurrentPov({
        h: Number(h),
        v: Number(v),
        z
      })

      setCurrentViewer((prev) => ({
        ...prev,
        selectedFloor: Number(targetFloor),
        selectedPoint: targetPoint,
        isClickToDirection: true,
        isSelectPointInLeftMenu: false
      }))
    }
  }

  const addPropertiesToKrpano = (
    krpano: any,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[]
  ) => {
    annotations && Krpano.AddAnnotation(krpano, annotations)
    directions && Krpano.AddDirectionArrow(krpano, directions)
    stairways && Krpano.AddStairway(krpano, stairways)
  }

  const krpanoOnreadyCallback = (
    krpano: any,
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[]
  ) => {
    const { h, v, z, multires, imageUrl, previewUrl, tilesUrl } =
      krpanoInitProps

    const varString = `layer[set_first_view_button].visible=true`
    krpano.call(
      `loadpano('/krpano/xml-assets/pano_view.xml','${varString}',IGNOREKEEP,BLEND(0.1));`
    )

    const sceneName = Krpano.getScenceNameByFileId(
      currentViewer.selectedPoint,
      points,
      currentViewer.selectedImage
    )
    const sceneString = Krpano.generateSceneXML({
      sceneName,
      tilesUrl: tilesUrl || undefined,
      multires: multires || undefined,
      s3Path: imageUrl,
      thumbnailUrl: previewUrl || ''
    })

    krpano.call(`includexmlstring(${sceneString}, null, null, false)`)

    if (!isFistLoading) {
      krpano.call(`load_scene(${sceneName},${h},${v},${z})`)
    }
    addPropertiesToKrpano(krpano, annotations, directions, stairways)
    setIsFistLoading(false)
  }

  const embedpano = (
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[],
    pointDetail?: Point
  ) => {
    const embedpano = document.getElementById('embedpano-viewer') as any
    const target = document.getElementById('viewer-pano-container')
    const { multires, imageUrl, previewUrl, tilesUrl, h, v, z } =
      krpanoInitProps
    let currentSelectedPoint = currentViewer.selectedPoint
    let currentSelectedImage = currentViewer.selectedImage
    if (pointDetail) {
      currentSelectedImage = pointDetail.image360?.id
      currentSelectedPoint = pointDetail.id
    }
    if (target) {
      const stairList = stairways?.map((item) => ({
        ...item,
        nextFloorName: building?.floors.find(
          (floor) => floor.id === +item.nextFloorId
        )?.name
      }))

      if (!embedpano) {
        ;(window as any).embedpano({
          id: 'embedpano-viewer',
          xml: null,
          target: 'viewer-pano-container',
          html5: 'auto+webgl',
          bgcolor: '#FFFFFF',
          onready: (krpano: any) => {
            krpanoOnreadyCallback(
              krpano,
              krpanoInitProps,
              annotations,
              directions,
              stairList
            )
          }
        })
      } else {
        if (!currentViewer.isSelectPointInLeftMenu) {
          const sceneName = Krpano.getScenceNameByFileId(
            currentSelectedPoint,
            points,
            currentSelectedImage
          )

          if (embedpano.get(`scene[${sceneName}]`) === null) {
            const sceneString = Krpano.generateSceneXML({
              sceneName,
              tilesUrl: tilesUrl || undefined,
              multires: multires || undefined,
              s3Path: imageUrl,
              thumbnailUrl: previewUrl || ''
            })

            embedpano.call(
              `includexmlstring(${sceneString}, null, null, false)`
            )
          }

          embedpano.call(`load_scene(${sceneName},${h},${v},${z})`)
        }
        addPropertiesToKrpano(embedpano, annotations, directions, stairList)
      }
    }
  }

  // handle event view around
  handleRedirect()
  handleClickAnnotation()
  handleChangeFloor()

  useEffect(() => {
    const listPointTo = points
      .find((item) => item.id === currentViewer.selectedPoint)
      ?.directions?.map((item) => item.point_to)
    if (!listPointTo?.length) {
      return
    }

    const loadScenes = async () => {
      const embedpano = document.getElementById('embedpano-viewer') as any
      if (!embedpano || !embedpano.get) {
        setTimeout(loadScenes, 100)
        return
      }

      await getSenceData(
        {
          method: 'post',
          data: {
            building_id: Number(id),
            floor_id: Number(currentViewer.selectedFloor),
            point_ids: listPointTo
          }
        },
        {
          onSuccess: (data: SenceDataResponse) => {
            try {
              const currentImageId = points.find(
                (p) => p.id === currentViewer.selectedPoint
              )?.image360?.id

              const items: SenceData[] = []
              data.data.forEach((item) => {
                if (
                  embedpano.get(
                    `scene[${Krpano.getScenceNameByFileId(
                      currentViewer.selectedPoint,
                      points,
                      item.id
                    )}]`
                  ) === null &&
                  item.id !== currentImageId
                ) {
                  items.push(item)
                }
              })
              if (items.length) {
                const sceneString = Krpano.generateSceneXMLs(items)
                embedpano.call(
                  `includexmlstring(${sceneString},null, null, false)`
                )
              }
            } catch (error) {}
          },
          onError: () => {}
        }
      )
    }

    loadScenes()
    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewer.selectedImage])

  useEffect(() => {
    return () => {
      Krpano.UnmountPanoViewer()
    }
  }, [])

  useEffect(() => {
    refreshBuilding()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewer.selectedFloor])

  return {
    mutate,
    currentViewer,
    setCurrentViewer,
    currentPov,
    setCurrentHgap,
    embedpano
  }
}
